import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Stack } from '@mui/material'
import { observer } from 'mobx-react'
import React from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { useInvalidateQueries } from '../../../../../hooks/useInvalidateQueries'
import { useTerritoryFields } from '../../../../../hooks/useTerritoryFields'
import { updateRequest } from '../../../../../services/ApiService'
import { notifySuccess } from '../../../../../services/ToastifyService/ToastifyService'
import {
  closeModalFn,
  openModalFn,
} from '../../../../../utils/controllers/ModalController'
import { getComp } from '../../../../../utils/utils/DI'
import singleRequestStore from '../../../store/SingleRequestStore'
import { RequestType } from '../../../types/Request.type'
import { OptionType } from '../elements/FormAutocomplete'
import PaymentAccordion from '../elements/PaymentAccordion'
import { getDefaultValues } from '../helpers/getDefaultValues'
import {
  ChangePaymentFormType,
  changePaymentSchema,
} from '../helpers/getFormSchema'
import requestDrawerStore from '../RequestDrawerStore'

type DrawerContentPaymentProps = {
  request: RequestType
}

type ValidParamsType = Record<
  keyof ChangePaymentFormType,
  string | OptionType | number | undefined | null
>
export const DrawerContentPayment: React.FC<DrawerContentPaymentProps> = ({
  request,
}) => {
  const invalidate = useInvalidateQueries()
  const { goBack } = requestDrawerStore
  const { updateRequest: handleUpdateRequest, updateHistory } =
    singleRequestStore

  const territoryStore = getComp('TerritoryStore')
  const statusesStore = getComp('StatusesStore')
  const { getInstallationReasonName } = getComp('InstallationReasonStore')
  const reasonName = getInstallationReasonName(request.reasonId)

  const defaultValues = React.useMemo(
    () => getDefaultValues(request, statusesStore.financialSources, reasonName),
    [request],
  )

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { dirtyFields, isValid },
  } = useForm<ChangePaymentFormType>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(changePaymentSchema(territoryStore.collection)),
  })

  const reason = watch('reasonId')

  const {
    onTerritoryChange,
    onDistrictChange,
    fullDotationRegions,
    reasonsItems,
    districtOptions,
    localitiesOptions,
    locality,
    paymentItems,
  } = useTerritoryFields(watch, setValue, reason?.key ?? -1)

  const onSubmit = async (data: ChangePaymentFormType) => {
    const validKeys = getValidKeys(request, data)
    const validData = validKeys.reduce((acc, el) => {
      if (typeof data[el] === 'object') {
        acc[el] = (data[el] as OptionType)?.key
      } else {
        acc[el] = data[el]
      }

      return acc
    }, {} as ValidParamsType)

    openModalFn['progress-backdrop']?.()
    await updateRequest(request.id, validData)
      .then((res: RequestType) => {
        handleUpdateRequest(res)
        invalidate(['requests-table'])
        updateHistory()
        notifySuccess('Заявка обновлена')
        goBack()
      })
      .finally(() => {
        closeModalFn['progress-backdrop']?.()
      })
  }

  const isPaymentTypeSelected =
    !!request.financialSourceId && request.financialSourceId !== 4

  const disableSaveBtn = !isValid || !Object.keys(dirtyFields).length

  return (
    <Stack
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      height={'100%'}
      justifyContent={'space-between'}
    >
      <PaymentAccordion
        control={control}
        districtOptions={districtOptions}
        fullDotationRegions={fullDotationRegions}
        isPaymentTypeSelected={isPaymentTypeSelected}
        localitiesOptions={localitiesOptions}
        locality={locality}
        paymentItems={paymentItems}
        reasonsItems={reasonsItems}
        request={request}
        onTerritoryChange={onTerritoryChange}
        onDistrictChange={onDistrictChange}
      />
      <Box px={2}>
        <Button
          color={'primary'}
          variant={'contained'}
          sx={{ margin: '24px 0 0 0' }}
          fullWidth
          type={'submit'}
          disabled={disableSaveBtn}
        >
          <FormattedMessage id={'сохранить'} />
        </Button>
        <Button
          color={'primary'}
          variant={'outlined'}
          onClick={() => goBack()}
          fullWidth
          disabled={disableSaveBtn}
          sx={{ margin: '8px 0 24px 0' }}
        >
          <FormattedMessage id={'Отмена'} />
        </Button>
      </Box>
    </Stack>
  )
}

const getValidKeys = (
  request: RequestType,
  data: ChangePaymentFormType,
): Array<keyof ChangePaymentFormType> => {
  const keys = (Object.keys(data) as Array<keyof ChangePaymentFormType>).filter(
    (key) => {
      if (typeof data[key] === 'object') {
        return (
          request[key as keyof RequestType] !== (data[key] as OptionType)?.key
        )
      } else {
        return request[key as keyof RequestType] !== data[key]
      }
    },
  )

  return keys as Array<keyof ChangePaymentFormType>
}

export default observer(DrawerContentPayment)
